import { useCached3DObject } from "@/object-cache";
import {
  selectClippingBoxEnabledForSheetCadAlignment,
  selectSheetElevation,
} from "@/store/modes/sheet-cad-alignment-mode-selectors";
import { setSheetElevation } from "@/store/modes/sheet-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { View } from "@faro-lotv/app-component-toolbox";
import { IElementModel3dStream } from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { useCallback, type JSX } from "react";
import { Box3 } from "three";
import { useNewPerspectiveCamera } from "../alignment-modes-commons/align-to-cad-utils";
import { ModelElevationScene } from "../alignment-modes-commons/model-elevation-scene";
import { useOverlayElements } from "../overlay-elements-context";

type SetHeightSceneProps = {
  /** The active cad model for alignment */
  activeCad: IElementModel3dStream;

  /** The bounding box of the cad model in world */
  cadBox: Box3;
};

/** @returns the scene for set the cad height */
export function SetHeightScene({
  activeCad,
  cadBox,
}: SetHeightSceneProps): JSX.Element {
  const cadModelObject = useCached3DObject(activeCad, "SetHeightScene");
  const dispatch = useAppDispatch();

  const { singleScreen } = useOverlayElements();
  const background = useThree((s) => s.scene.background);

  const camera = useNewPerspectiveCamera();

  const isClippingBoxEnabled = useAppSelector(
    selectClippingBoxEnabledForSheetCadAlignment,
  );

  const sheetElevation = useAppSelector(selectSheetElevation);

  const updateElevation = useCallback(
    (elevation: number) => {
      dispatch(setSheetElevation(elevation));
    },
    [dispatch],
  );

  return (
    <View
      camera={camera}
      trackingElement={singleScreen}
      background={background}
      hasSeparateScene
    >
      <ModelElevationScene
        cadModelObject={cadModelObject}
        cadBox={cadBox}
        elevation={sheetElevation}
        updateElevation={updateElevation}
        isClippingBoxEnabled={isClippingBoxEnabled}
        camera={camera}
      />
    </View>
  );
}
